import { useCallback } from 'react';
import { gtmAddEvent } from '@lingoda/analytics';
import { type User, userSelector } from '@lingoda/auth';
import {
    type ApolloError,
    type ChangeCurrentSubscriptionMutation,
    UserSubscriptionsDocument,
    useChangeCurrentSubscriptionMutation,
} from '@lingoda/monolith-graphql';
import { useDispatch, useSelector } from '@lingoda/hooks';
import { UserSubscriptionsFullDocument } from '../queries.monolith.generated';
import { fetchSubscriptions } from '../actions';
import type { ChangeSubscriptionPurchase } from '../types';

interface UseChangeSubscriptionOptions {
    onCompleted?: (data: ChangeCurrentSubscriptionMutation) => void;
    onError?: (error: ApolloError) => void;
}

export const useChangeSubscription = (options: UseChangeSubscriptionOptions = {}) => {
    const dispatch = useDispatch();

    const user = useSelector(userSelector);

    const [change, { loading }] = useChangeCurrentSubscriptionMutation({
        refetchQueries: [
            UserSubscriptionsDocument, // temporary until we get rid of the old types and queries
            UserSubscriptionsFullDocument,
        ],
        awaitRefetchQueries: true,
        onCompleted(data) {
            // Is used for updating redux store, until we get rid of it completely
            dispatch(fetchSubscriptions());
            options?.onCompleted?.(data);
        },
        onError(error) {
            options?.onError?.(error);
        },
    });

    const changeSubscription = useCallback(
        async (purchase: ChangeSubscriptionPurchase, agreements?: string[]) => {
            const result = await change({ variables: { purchaseId: purchase.id, agreements } });

            if (user) {
                addSubscriptionChangeEvent(user, purchase);
            }

            return result;
        },
        [change, user],
    );

    return [changeSubscription, { loading }] as const;
};

const addSubscriptionChangeEvent = (user: User, purchase: ChangeSubscriptionPurchase) => {
    gtmAddEvent('subscriptionChanged', {
        transactionRevenue: `${purchase.chargeTotal.amountInMajor}`,
        transactionCurrency: purchase.chargeTotal.currency,
        product_type: purchase.type,
        product_name: `${purchase.displayName}`,
        purchase_type:
            purchase.__typename === 'LegacyPurchase' && purchase.withTrial ? 'trial' : 'direct',
        discountValue: `${purchase.discount.amountInMajor}`,
        promoCode: purchase.coupons.map((coupon) => coupon.code).join(', '),
        userEmail: user?.email ?? '',
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        isFirstSubscription: 'false',
    });
};
